import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { MagnifyingGlass, UsersThree, CalendarBlank, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function ProjectAssignmentsSheet({ project, open, onOpenChange, onChanged }) {
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        if (!project?.project_id) return;
        setLoading(true);
        try {
            const r = await http.get(`/projects/${project.project_id}/assignments`);
            setUsers(r.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, project?.project_id]);

    const toggle = async (u, value) => {
        try {
            if (value) {
                await http.post(`/projects/${project.project_id}/assignments/${u.user_id}`, {
                    date_start: null,
                    date_end: null,
                });
            } else {
                await http.delete(
                    `/projects/${project.project_id}/assignments/${u.user_id}`,
                );
            }
            setUsers((prev) =>
                prev.map((x) =>
                    x.user_id === u.user_id
                        ? { ...x, assigned: value, date_start: null, date_end: null }
                        : x,
                ),
            );
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
            load();
        }
    };

    const saveDates = async (u, date_start, date_end) => {
        try {
            await http.patch(
                `/projects/${project.project_id}/assignments/${u.user_id}`,
                { date_start: date_start || null, date_end: date_end || null },
            );
            setUsers((prev) =>
                prev.map((x) =>
                    x.user_id === u.user_id
                        ? { ...x, date_start: date_start || null, date_end: date_end || null }
                        : x,
                ),
            );
            toast.success("Fechas actualizadas");
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const filtered = users.filter(
        (u) =>
            !q ||
            u.name.toLowerCase().includes(q.toLowerCase()) ||
            u.email.toLowerCase().includes(q.toLowerCase()),
    );
    const assignedCount = users.filter((u) => u.assigned).length;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="rounded-none sm:max-w-lg w-full flex flex-col">
                <SheetHeader>
                    <SheetTitle className="font-display">
                        Asignaciones del proyecto
                    </SheetTitle>
                    <SheetDescription className="text-xs text-slate-500">
                        <span className="font-medium text-slate-700">{project?.name}</span>
                        {project?.project_code && (
                            <span className="font-mono ml-2">· {project.project_code}</span>
                        )}
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-4 relative">
                    <MagnifyingGlass
                        size={14}
                        className="absolute left-2 top-2.5 text-slate-400"
                    />
                    <Input
                        placeholder="Buscar empleado…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="rounded-sm border-slate-300 pl-7"
                        data-testid="proj-assign-search"
                    />
                </div>

                <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                    <span className="flex items-center gap-1">
                        <UsersThree size={12} />
                        {assignedCount} asignados
                    </span>
                    <span>{filtered.length} resultados</span>
                </div>

                <div className="border border-slate-200 mt-2 flex-1 overflow-y-auto">
                    {loading && (
                        <div className="text-center py-6 text-slate-400 text-sm">
                            Cargando…
                        </div>
                    )}
                    {!loading && filtered.length === 0 && (
                        <div className="text-center py-12 text-slate-500 text-sm">
                            Sin resultados.
                        </div>
                    )}
                    {filtered.map((u) => (
                        <AssignmentRow
                            key={u.user_id}
                            user={u}
                            onToggle={toggle}
                            onSaveDates={saveDates}
                        />
                    ))}
                </div>
            </SheetContent>
        </Sheet>
    );
}

function AssignmentRow({ user, onToggle, onSaveDates }) {
    const [start, setStart] = useState(user.date_start || "");
    const [end, setEnd] = useState(user.date_end || "");

    useEffect(() => {
        setStart(user.date_start || "");
        setEnd(user.date_end || "");
    }, [user.date_start, user.date_end]);

    const today = todayIso();
    const expired = user.date_end && user.date_end < today;
    const future = user.date_start && user.date_start > today;

    return (
        <div
            className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50"
            data-testid={`proj-assign-row-${user.user_id}`}
        >
            {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 object-cover" />
            ) : (
                <div className="w-8 h-8 bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
                    {user.name?.[0]?.toUpperCase()}
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                    {user.name}
                    {expired && (
                        <span
                            className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5"
                            title="Asignación finalizada"
                        >
                            <Warning size={10} /> Cerrada
                        </span>
                    )}
                    {future && (
                        <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-800 px-1.5 py-0.5">
                            Futura
                        </span>
                    )}
                </div>
                <div className="text-[11px] font-mono text-slate-500 truncate">
                    {user.email}
                </div>
                {user.assigned && (
                    <Popover>
                        <PopoverTrigger asChild>
                            <button
                                className="text-[11px] text-slate-500 hover:text-[#002BAF] mt-1 flex items-center gap-1"
                                data-testid={`proj-assign-dates-${user.user_id}`}
                            >
                                <CalendarBlank size={11} />
                                {user.date_start || user.date_end ? (
                                    <span className="font-mono">
                                        {user.date_start || "—"} →{" "}
                                        {user.date_end || "abierta"}
                                    </span>
                                ) : (
                                    <span>Sin fechas (abierta)</span>
                                )}
                            </button>
                        </PopoverTrigger>
                        <PopoverContent
                            className="w-72 rounded-sm p-4"
                            data-testid={`proj-assign-dates-popover-${user.user_id}`}
                        >
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                                Validez de la asignación
                            </div>
                            <label className="text-xs text-slate-600 block">Desde</label>
                            <Input
                                type="date"
                                value={start}
                                onChange={(e) => setStart(e.target.value)}
                                className="mt-1 rounded-sm border-slate-300 font-mono"
                                data-testid={`proj-assign-start-${user.user_id}`}
                            />
                            <label className="text-xs text-slate-600 block mt-2">
                                Hasta
                            </label>
                            <Input
                                type="date"
                                value={end}
                                onChange={(e) => setEnd(e.target.value)}
                                className="mt-1 rounded-sm border-slate-300 font-mono"
                                data-testid={`proj-assign-end-${user.user_id}`}
                            />
                            <div className="text-[11px] text-slate-500 mt-2">
                                Si la fecha "Hasta" es anterior a hoy, el proyecto
                                desaparece del parte del empleado.
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button
                                    onClick={() => {
                                        setStart("");
                                        setEnd("");
                                        onSaveDates(user, "", "");
                                    }}
                                    className="text-xs text-slate-500 hover:text-slate-800 underline"
                                    data-testid={`proj-assign-clear-${user.user_id}`}
                                >
                                    Limpiar
                                </button>
                                <button
                                    onClick={() => onSaveDates(user, start, end)}
                                    className="ml-auto px-3 py-1 rounded-sm bg-[#002BAF] text-white text-xs hover:bg-[#001F82]"
                                    data-testid={`proj-assign-save-dates-${user.user_id}`}
                                >
                                    Guardar
                                </button>
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
            </div>
            <Switch
                checked={user.assigned}
                onCheckedChange={(v) => onToggle(user, v)}
                data-testid={`proj-assign-toggle-${user.user_id}`}
            />
        </div>
    );
}
